import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import ticketScheme from "./TicketValidationScheme";
import ServiceTypeSection from "./TicketFormSteps/ServiceTypeSection";
import DetailsSection from "./TicketFormSteps/DetailsSection";
import ImageUploadSection from "./TicketFormSteps/ImageUploadSection";
import { createNewTicketThunk } from "../../modules/ticket/ticketForm/ticketFormOperations";
import { showNotificationMessage } from "../../modules/uiState";
import { route as ticketsListRoute } from "../TicketListContainer/TicketListScreen";
import { relationTypes } from "./relationTypes";
import { selectCurrentUser } from "../../modules/user/userActions";
import { currentEstateSelectors } from "../../modules/estate/currentEstate/currentEstateSlice";
import { ButtonBase, Divider, Paper } from "@mui/material";
import TicketFormHeader from "./TicketFormHeader";
import DocumentLayout from "../../components/Layouts/DocumentLayout";
import { ErrorOutline } from "@mui/icons-material";

/**
 * Ticket form component using React hooks instead of HOCs
 */
const TicketForm = () => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const currentEstate = useSelector(currentEstateSelectors.selectCurrentEstate);

  const [page, setPageState] = useState(1);

  const next = (ev) => {
    if (ev && ev.preventDefault) ev.preventDefault();
    setPageState((p) => p + 1);
  };

  const previous = (ev) => {
    if (ev && ev.preventDefault) ev.preventDefault();
    setPageState((p) => Math.max(1, p - 1));
  };

  const formik = useFormik({
    initialValues: {
      estateId: currentEstate?.id || "",
      body: "",
      place: "",
      serviceType: 0,
      capture: [],
      anonymous: false,
      private: true,
    },
    validationSchema: ticketScheme,
    onSubmit: (values, { resetForm, setFieldError, setSubmitting }) => {
      const {
        estateId,
        subject,
        body,
        place,
        serviceType,
        capture,
        anonymous,
      } = values;

      const ticket = {
        estateId,
        subject,
        messagesEnabled: true,
        anonymous,
        body: `
        ${body || ""}
        ---
        ${place || ""}
      `,
      };

      if (!Boolean(subject)) {
        setFieldError(
          "subject",
          intl.formatMessage({ id: "ticket.create.submit.error.no_subject" }),
        );
        setSubmitting(false);
        return;
      }

      if (Boolean(values.private)) {
        ticket.accessibleByPersonIds = [currentUser.id];
        ticket.publishingMode = "UNPUBLISHED";
        ticket.accessibleByTypes = [];
      } else {
        ticket.accessibleByTypes = relationTypes.map(({ id }) => id);
        ticket.publishingMode = "PUBLISHED";
        ticket.accessibleByPersonIds = [];
      }

      if (Boolean(values.anonymous) && currentUser.relations) {
        const livingUnitIds = currentUser.relations
          .filter((estate) => estateId === estate.estateId)
          .map(({ livingUnitId }) => livingUnitId);
        ticket.livingUnitIds = livingUnitIds;
      }

      if (Number(serviceType)) {
        ticket.serviceType = { id: serviceType };
      }

      ticket.attachments = capture;

      dispatch(createNewTicketThunk(ticket))
        .then(() => {
          resetForm();
          dispatch(
            showNotificationMessage(
              intl.formatMessage({ id: "ticket.create.submit.success" }),
            ),
          );
          history.push(ticketsListRoute);
        })
        .catch((err) => {
          console.error(err);
          dispatch(
            showNotificationMessage(
              intl.formatMessage({ id: "ticket.create.submit.error" }),
            ),
          );
        })
        .finally(() => setSubmitting(false));
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = formik;

  const serviceTypes = (currentEstate && currentEstate.serviceTypes) || [];
  const isLastPage = page === 3;

  // Keep estateId in sync with currentEstate when available
  useEffect(() => {
    if (currentEstate && currentEstate.id && setFieldValue) {
      setFieldValue("estateId", currentEstate.id);
    }
  }, [currentEstate, setFieldValue]);

  return (
    <DocumentLayout>
      <Paper sx={{ py: 3 }}>
        <form
          onSubmit={handleSubmit}
          autoComplete="off"
          encType="multipart/form-data"
        >
          <DocumentLayout>
            <Box sx={{ mb: 2 }}>
              <TicketFormHeader
                page={page}
                values={values}
                serviceTypes={serviceTypes}
              />
            </Box>
          </DocumentLayout>

          {(() => {
            switch (page) {
              case 1:
                return (
                  <ServiceTypeSection
                    serviceTypes={serviceTypes}
                    onNext={next}
                    handleSetFieldValue={(field, value) =>
                      setFieldValue(field, String(value))
                    }
                  />
                );
              case 2:
                return (
                  <DetailsSection
                    error={errors}
                    touched={touched}
                    values={values}
                    handleChange={handleChange}
                  />
                );
              case 3:
                return (
                  <ImageUploadSection
                    values={values}
                    handleSetFieldValue={(field, value) =>
                      setFieldValue(field, value)
                    }
                  />
                );
              default:
                return null;
            }
          })()}

          <DocumentLayout>
            {page > 1 && <Divider sx={{ my: 2 }} />}

            {errors.subject ? (
              <>
                <ButtonBase onClick={previous} variant="outlined" fullWidth>
                  <Typography
                    variant="body1"
                    color="error"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <ErrorOutline /> {errors.subject}
                  </Typography>
                </ButtonBase>
                <Divider sx={{ my: 2 }} />
              </>
            ) : null}

            {isLastPage ? (
              <>
                <Button
                  size="large"
                  sx={{
                    mb: 1,
                  }}
                  variant="outlined"
                  fullWidth
                  onClick={previous}
                >
                  {intl.formatMessage({
                    id: "ticket.create.form.button.label.back",
                  })}
                </Button>
                <Button
                  size="large"
                  fullWidth
                  variant="contained"
                  color="secondary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {intl.formatMessage({
                    id: "ticket.create.form.button.label.submit",
                  })}
                </Button>
              </>
            ) : (
              <>
                {page > 1 && (
                  <Button
                    size="large"
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 1 }}
                    onClick={previous}
                  >
                    {intl.formatMessage({
                      id: "ticket.create.form.button.label.back",
                    })}
                  </Button>
                )}
                {page > 1 && (
                  <Button
                    size="large"
                    variant="contained"
                    color="secondary"
                    fullWidth
                    onClick={next}
                  >
                    {intl.formatMessage({
                      id: "ticket.create.form.button.label.next",
                    })}
                  </Button>
                )}
              </>
            )}
          </DocumentLayout>
        </form>
      </Paper>
    </DocumentLayout>
  );
};

export default TicketForm;
